import { useCallback, useEffect, useState } from 'react';

import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';

import Icon from '@cobliteam/ui/icons';

import { UserRole } from '@cobliteam/common-types';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';

const validUserRoles: UserRole[] = [UserRole.ADMINISTRATOR, UserRole.MANAGER, UserRole.MAP_VIEW, UserRole.OPERATION, UserRole.USER_INSTALLER];

const getUserRoleLabel = (role: UserRole) => {
  switch (role) {
    case UserRole.ADMINISTRATOR:
      return 'Administrador';
    case UserRole.MANAGER:
      return 'Gestor de frota';
    case UserRole.MAP_VIEW:
      return 'Visualização de mapa';
    case UserRole.OPERATION:
      return 'Operação';
    case UserRole.USER_INSTALLER:
      return 'Instalador';
  }
};

interface MultiSelectUserRolesProps {
  size?: 'sm' | 'md';
  selectedRoles: UserRole[];
  onChange: (roles: UserRole[]) => void;
}

const MultiSelectUserRoles = ({ selectedRoles, onChange, size = 'md' }: MultiSelectUserRolesProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedRoles || []);

  const hasNotChanged = selectedRoles?.length === draft?.length && selectedRoles.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedRoles || []);
  }, [selectedRoles]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (roleItem: UserRole) => {
      const existItem = draft.includes(roleItem);
      const newDraft = existItem ? draft.filter((value) => value !== roleItem) : [...draft, roleItem];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='outlined' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              Limpar tudo
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightItem={30}
              data={validUserRoles}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: UserRole) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getUserRoleLabel(item)}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='text' full size='sm' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<Icon name='cog' />}>
        Função {(selectedRoles?.length || 0) > 0 ? `(${selectedRoles?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectUserRoles;
